import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, mergeMap, Subject, Subscription, takeUntil } from 'rxjs';
import { AuthService } from './Services/Auth/auth.service';
import { CanActivateService } from './Services/CanActivate/can-activate.service';

import { UserProfileData } from './interfaces/user-profile-data';
import { LoadingService } from './Services/Loading/loading-service.service';

import { MatDialog } from '@angular/material/dialog';

import { BackendAccessService } from './Services/APICalls/backend-access-service';
import { FeatureFlagsService } from './Services/feature-flags.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  private subscription!: Subscription;
  private destroy$ = new Subject<void>();
  title = 'Allegion License Management';
  vegaUpdateInterval: number = 0;
  APPLICATION_TIMEOUT_TIME = 1000 * 30 * 60; //30 minutes
  userActivity: ReturnType<typeof setTimeout>;
  userInactive: Subject<any> = new Subject();
  authService: AuthService;
  visibility: boolean = true;
  browserLang: string | undefined;
  loading!: boolean;
  isLoading = false;
  private loadingSubscription!: Subscription;
  userAuth: any;
  accessToken: any;
  private multipleBranchesSubscription!: Subscription;
  showEngageNavbar = false;
  showOnboardingNavBar = false;
  isNavbarCollapsed = false;

  constructor(
    private _translate: TranslateService,
    public auth: AuthService,
    public userRole: CanActivateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loadingService: LoadingService,
    private featureFlags: FeatureFlagsService,
    private canActivateService: CanActivateService,
    private dialog: MatDialog,
    private backend: BackendAccessService
  ) {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        // Now TypeScript knows event is of type NavigationEnd
        this.showEngageNavbar =
          event.urlAfterRedirects.includes(
            'engage-schlage-mobile-access-credentials'
          ) ||
          event.urlAfterRedirects.includes('engage-transfer') ||
          event.urlAfterRedirects.includes('support-ticket-engage');
      });

    this.subscription = this.auth
      .getTokenSilently$()
      .subscribe((response: any) => {
        this.accessToken = response;
      });
    this.userActivity = setTimeout(
      () => this.userInactive.next(undefined),
      this.APPLICATION_TIMEOUT_TIME
    );
    this.userInactive.subscribe(() => this.timeoutDetected());

    this.authService = auth;
    _translate.addLangs(['en', 'fr', 'es']);
    _translate.setDefaultLang('en');
    this.browserLang = _translate.getBrowserLang();
    if (this.browserLang) {
      _translate.use(
        this.browserLang.match(/en|fr|es/) ? this.browserLang : 'en'
      );
      // _translate.use('fr');
    }
    this.auth
      .getTokenSilently$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.accessToken = response;
      });
  }

  /* istanbul ignore next */
  ngOnInit() {
    this.loadingService.show();
    this.loadingSubscription = this.loadingService.isLoading$.subscribe(
      (isLoading: boolean) => {
        this.isLoading = isLoading;
      }
    );
    this.vegaUpdateInterval = setInterval(
      () => this.userInactive.next(undefined),
      this.APPLICATION_TIMEOUT_TIME
    ) as any;
    this.router.events
      .pipe(
        filter((events) => events instanceof NavigationEnd),
        map((evt) => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .pipe(
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((x) => {
        x['header'] === true
          ? (this.visibility = true) && (this.loading = false)
          : (this.visibility = false);

        this.authService
          .getUserProfile$()
          .subscribe((results: UserProfileData) => {
            if (!results) {
              return;
            }
            this.loadingService.hide();
          });
      });
  }
  /* istanbul ignore next */
  timeoutDetected() {
    console.log('***** Timer Logging out');
    this.authService.logout();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  @HostListener('window:mousemove')
  @HostListener('document:click')
  @HostListener('document:wheel')
  @HostListener('document:scroll')
  @HostListener('document:mousemove')
  @HostListener('document:keyup')
  refreshUserState() {
    clearTimeout(this.userActivity);
    this.userActivity = setTimeout(
      () => this.userInactive.next(undefined),
      this.APPLICATION_TIMEOUT_TIME
    );
  }
  sideBarExtended: boolean = false;

  toggleSideBarExtended(sideBarState: boolean) {
    console.log('sideBar extended:', sideBarState);
    this.sideBarExtended = sideBarState;
  }
  /* istanbul ignore next */
  ngOnDestroy() {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.multipleBranchesSubscription) {
      this.multipleBranchesSubscription.unsubscribe();
    }
  }
  /* istanbul ignore next */
  onCollapseChange(isCollapsed: boolean) {
    this.isNavbarCollapsed = isCollapsed;
  }
}
